import React, {FC, useState} from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {GET_PALOWAN_VACATION_ASSIGNMENTS} from 'graphql/palowan-vacation/Queries';
import {
  PalowanVacationAssignment,
  PalowanVacationAssignmentConnection,
  PalowanWithAssignmentCount,
} from 'models/PalowanVacationAssignment';
import PalowanRangeInlineTable from './PalowanRangeInlineTable';
import PalowanVacationDisableModal from 'viewComponents/PalowanVacationDisableModal/PalowanVacationDisableModal';
import PalowanVacationEnableModal from 'viewComponents/PalowanVacationEnableModal/PalowanVacationEnableModal';

export interface PalowanRangesModalProps {
  /** Whether the dialog is visible. */
  open: boolean;
  /** The palowan whose ranges are being managed, or null when no selection. */
  palowan: PalowanWithAssignmentCount | null;
  /** When false, write actions (create, edit) are disabled. */
  isCountryActive: boolean;
  /** Callback invoked when the dialog is dismissed. */
  onClose: () => void;
  /** Callback invoked when a mutation changed the ranges so the parent can refresh counts. */
  onRangesChanged: () => void;
}

/**
 * Modal dialog that displays and manages all vacation seniority ranges for a single palowan.
 * Fetches the assignment list on open and delegates inline editing to PalowanRangeInlineTable.
 * Disable and enable actions open confirmation modals.
 *
 * @param open - Whether the dialog is visible.
 * @param palowan - Selected palowan, or null when closed.
 * @param isCountryActive - Disables write actions when false.
 * @param onClose - Dismiss handler; also triggers onRangesChanged to refresh the parent table.
 * @param onRangesChanged - Refresh callback called after any successful mutation or on close.
 * @returns A MUI Dialog, or null when palowan is null.
 */
const PalowanRangesModal: FC<PalowanRangesModalProps> = ({
  open,
  palowan,
  isCountryActive,
  onClose,
  onRangesChanged,
}) => {
  const {t} = useTranslation();
  const [isAddingNewRow, setIsAddingNewRow] = useState(false);
  const [disableTarget, setDisableTarget] =
    useState<PalowanVacationAssignment | null>(null);
  const [enableTarget, setEnableTarget] =
    useState<PalowanVacationAssignment | null>(null);

  // Fetch all assignment ranges for this palowan when the modal is open.
  // pageSize=100 loads all ranges in a single request since a palowan is unlikely
  // to have more than a handful of ranges.
  const {data, loading, refetch} = useQuery<{
    getPalowanVacationAssignments: PalowanVacationAssignmentConnection;
  }>(GET_PALOWAN_VACATION_ASSIGNMENTS, {
    variables: {
      palowanId: palowan?.palowanId,
      pageSize: 100,
    },
    skip: !open || palowan === null,
    fetchPolicy: 'cache-and-network',
  });

  const assignments: PalowanVacationAssignment[] =
    data?.getPalowanVacationAssignments?.edges?.map(e => e.node) ?? [];

  // True when a row is currently being edited (either a new or an existing row),
  // used to disable the "New parameter" button to prevent conflicting edit states.
  const hasActiveRow = isAddingNewRow;

  const handleMutationSuccess = () => {
    refetch();
    onRangesChanged();
  };

  const handleDisableSuccess = () => {
    setDisableTarget(null);
    refetch();
    onRangesChanged();
  };

  const handleEnableSuccess = () => {
    setEnableTarget(null);
    refetch();
    onRangesChanged();
  };

  const handleClose = () => {
    setIsAddingNewRow(false);
    onClose();
    onRangesChanged();
  };

  if (!palowan) return null;

  const palowanName = `${palowan.firstName} ${palowan.lastName}`;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        {/* Header: generic title + close icon with a light gray background. */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'grey.100',
            pr: 6,
          }}
        >
          {t('leaves.PalowanRangesModal.header')}
        </DialogTitle>
        {/* Close button anchored to the top-right corner, independent of the title layout. */}
        <IconButton
          aria-label="Close dialog"
          onClick={handleClose}
          sx={{position: 'absolute', top: 8, right: 8}}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{padding: '16px 16px'}}>
          {/* Sub-header inside the content: palowan name and add-parameter action. */}
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <span className="text-base font-semibold text-gray-800">
              {palowanName}
            </span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddingNewRow(true)}
              disabled={!isCountryActive || hasActiveRow}
              sx={{flexShrink: 0}}
            >
              {t('leaves.PalowanRangesModal.newParameterButton')}
            </Button>
          </div>
          <PalowanRangeInlineTable
            assignments={assignments}
            loading={loading}
            isCountryActive={isCountryActive}
            palowanId={palowan.palowanId}
            isAddingNewRow={isAddingNewRow}
            onDisable={setDisableTarget}
            onEnable={setEnableTarget}
            onMutationSuccess={handleMutationSuccess}
            onCancelNewRow={() => setIsAddingNewRow(false)}
          />
        </DialogContent>
      </Dialog>
      <PalowanVacationDisableModal
        open={disableTarget !== null}
        assignment={disableTarget}
        onClose={() => setDisableTarget(null)}
        onSuccess={handleDisableSuccess}
      />
      <PalowanVacationEnableModal
        open={enableTarget !== null}
        assignment={enableTarget}
        onClose={() => setEnableTarget(null)}
        onSuccess={handleEnableSuccess}
      />
    </>
  );
};

export default PalowanRangesModal;
